import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { clearToken, clearUser, getUser } from "../../lib/authToken";
import { DEV_USER } from "../../config/localAuth";
import {
  ProSidebar,
  Menu,
  MenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarContent,
} from "react-pro-sidebar";
import {
  LuLayoutDashboard,
  LuMessageSquare,
  LuCalendar,
  LuCreditCard,
  LuUtensils,
  LuActivity,
  // LuWarehouse,
  LuLogOut,
} from "react-icons/lu";
import { FiArrowLeftCircle, FiArrowRightCircle } from "react-icons/fi";

import "react-pro-sidebar/dist/css/styles.css";
import "./Sidebar.css";

// Navigation items with required permissions
const allNavItems = [
  { to: "/dashboard", label: "Dashboard", icon: LuLayoutDashboard, permission: "view_dashboard" },
  { to: "/orders", label: "Orders", icon: LuMessageSquare, permission: "view_orders" },
  { to: "/calendar", label: "Calendar", icon: LuCalendar, permission: "view_calendar" },
  { to: "/payments", label: "Payments", icon: LuCreditCard, permission: "view_payments" },
  { to: "/live-orders", label: "Live Orders", icon: LuActivity, permission: "view_live_orders" },
  { to: "/menu", label: "Menu", icon: LuUtensils, permission: "view_menu" },
  // { to: "/inventory-stock", label: "Inventories", icon: LuWarehouse, permission: "view_inventory" },
];

const Sidebar = ({ menuCollapse, setMenuCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  
  // Get user and determine accessible navigation
  const user = getUser() || DEV_USER;
  const userIsAdmin = user?.role === 'admin';
  
  // Filter navigation items based on role/permissions
  // Admin gets access to all items
  // Non-admin users only get: Live Orders, Orders, Menu
  const accessibleNavItems = userIsAdmin 
    ? allNavItems 
    : allNavItems.filter(item => {
        // Allow only specific pages for non-admin users
        const allowedPaths = ['/orders', '/live-orders', '/menu','/calendar'];
        return allowedPaths.includes(item.to);
      });

  const menuIconClick = () => {
    setMenuCollapse(!menuCollapse);
  };

  const handleLogout = () => {
    clearToken();
    clearUser();
    navigate("/home");
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <div id="sidebar" className={menuCollapse ? "collapsed" : ""}>
      <ProSidebar collapsed={menuCollapse} transitionDuration={300}>
        <SidebarHeader>
          <div className="logotext">
            <NavLink to="/dashboard" className="sb-logo" aria-label="Home">
              <div className="sb-logo-wrapper">
                <img
                  src="/logo_meallinkr.png"
                  alt="Meallinkr Logo"
                  className="sb-logo-img"
                />
                
              </div>
            </NavLink>
          </div>
          <button
            className="closemenu"
            onClick={menuIconClick}
            aria-label={menuCollapse ? "Expand sidebar" : "Collapse sidebar"}
          >
            {menuCollapse ? <FiArrowRightCircle /> : <FiArrowLeftCircle />}
          </button>
        </SidebarHeader>
        <SidebarContent>
          <Menu 
            iconShape="circle"
            menuItemStyles={{
              button: {
                [`&.active`]: {
                  backgroundColor: 'transparent',
                },
              },
            }}
          >
            {accessibleNavItems.map(({ to, label, icon: Icon }) => {
              const active = isActive(to);
              const handleItemClick = () => {
                navigate(to);
              };
              return (
                <MenuItem
                  key={to}
                  icon={<Icon size={22} />}
                  active={active}
                  onMouseEnter={() => setHoveredItem(to)}
                  onMouseLeave={() => setHoveredItem(null)}
                  onClick={handleItemClick}
                  className={`sb-menu-item ${active ? "active" : ""}`}
                >
                  {menuCollapse ? (
                    <span className="sb-link">
                      <span className="sb-link-text">{label}</span>
                      {active && <span className="sb-active-indicator" />}
                    </span>
                  ) : (
                    <NavLink
                      to={to}
                      className="sb-link"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(to);
                      }}
                    >
                      <span className="sb-link-text">{label}</span>
                      {active && <span className="sb-active-indicator" />}
                    </NavLink>
                  )}
                  {menuCollapse && hoveredItem === to && (
                    <div className="sb-tooltip">{label}</div>
                  )}
                </MenuItem>
              );
            })}
          </Menu>
        </SidebarContent>
        <SidebarFooter>
          <Menu iconShape="circle">
            <MenuItem
              icon={<LuLogOut size={22} />}
              onClick={handleLogout}
              className="sb-menu-item sb-logout-item"
              onMouseEnter={() => setHoveredItem("logout")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <span className="sb-label">Logout</span>
              {menuCollapse && hoveredItem === "logout" && (
                <div className="sb-tooltip">Logout</div>
              )}
            </MenuItem>
          </Menu>
        </SidebarFooter>
      </ProSidebar>
    </div>
  );
};

export default Sidebar;
